import { useMutation, useQuery } from '@apollo/client/react';
import {
  Box,
  Button,
  HStack,
  Input,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useState } from 'react';

import { toaster } from '#components/ui/toaster';
import { CREATE_REACTION } from '#graphql/mutations/create-reaction';
import { DELETE_REACTION } from '#graphql/mutations/delete-reaction';
import { GET_REACTIONS } from '#graphql/queries/reactions';

export function WorkspaceSettingsPage() {
  const { data, loading, error, refetch } = useQuery(GET_REACTIONS);
  const [createReaction, { loading: creating }] = useMutation(CREATE_REACTION);
  const [deleteReaction] = useMutation(DELETE_REACTION);
  const [id, setId] = useState('');
  const [value, setValue] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const rows = data?.reactions ?? [];
  const trimmedId = id.trim();
  const trimmedValue = value.trim();

  const handleCreate = async () => {
    if (!trimmedId || !trimmedValue) {
      return;
    }

    try {
      await createReaction({
        variables: {
          input: {
            id: trimmedId,
            value: trimmedValue,
          },
        },
      });
      await refetch();
      setId('');
      setValue('');
      toaster.create({
        title: 'Reaction added',
        type: 'success',
        duration: 2500,
        closable: true,
      });
    } catch (err) {
      toaster.create({
        title: 'Error',
        description:
          err instanceof Error ? err.message : 'Could not add reaction.',
        type: 'error',
      });
    }
  };

  const handleDelete = async (reactionId: string) => {
    if (
      !window.confirm(`Delete reaction "${reactionId}"? This cannot be undone.`)
    ) {
      return;
    }

    setDeletingId(reactionId);
    try {
      await deleteReaction({
        variables: { id: reactionId },
      });
      await refetch();
      toaster.create({
        title: 'Reaction removed',
        type: 'success',
        duration: 2500,
        closable: true,
      });
    } catch (err) {
      toaster.create({
        title: 'Error',
        description:
          err instanceof Error ? err.message : 'Could not delete reaction.',
        type: 'error',
      });
    } finally {
      setDeletingId(null);
    }
  };

  if (loading && !data) {
    return <Text>Loading...</Text>;
  }

  if (error) {
    return (
      <Box>
        <VStack gap={3} align="stretch">
          <Text color="red.500">Error: {error.message}</Text>
          <Button
            size="sm"
            alignSelf="flex-start"
            onClick={() => void refetch()}
          >
            Retry
          </Button>
        </VStack>
      </Box>
    );
  }

  return (
    <Box>
      <VStack gap={5} align="stretch">
        <Box>
          <Text fontSize="2xl" fontWeight="semibold" color="fg">
            Workspace Settings
          </Text>
          <Text color="fg.muted" fontSize="sm" mt={1}>
            Manage workspace reaction definitions used in message reaction
            pickers.
          </Text>
        </Box>

        <Box bg="bg" shadow="lg" borderRadius="lg" p={6}>
          <VStack gap={4} align="stretch">
            <Text fontSize="sm" fontWeight="medium" color="fg">
              Add reaction
            </Text>
            <HStack gap={2} align="flex-end" flexWrap="wrap">
              <Box>
                <Text fontSize="xs" color="fg.muted" mb={1}>
                  ID
                </Text>
                <Input
                  value={id}
                  onChange={(e) => {
                    setId(e.target.value);
                  }}
                  placeholder="e.g. joy"
                  maxW="xs"
                  disabled={creating}
                />
              </Box>
              <Box>
                <Text fontSize="xs" color="fg.muted" mb={1}>
                  Emoji value
                </Text>
                <Input
                  value={value}
                  onChange={(e) => {
                    setValue(e.target.value);
                  }}
                  placeholder="e.g. 😄"
                  maxW="xs"
                  disabled={creating}
                />
              </Box>
              <Button
                size="sm"
                onClick={() => {
                  void handleCreate();
                }}
                loading={creating}
                disabled={!trimmedId || !trimmedValue}
              >
                Add reaction
              </Button>
            </HStack>
          </VStack>
        </Box>

        <Box bg="bg" shadow="lg" borderRadius="lg" p={6}>
          <VStack gap={3} align="stretch">
            <HStack justify="space-between" align="center">
              <Text fontSize="sm" fontWeight="medium" color="fg">
                Reactions ({rows.length})
              </Text>
              <Button
                size="xs"
                variant="outline"
                onClick={() => {
                  void refetch();
                }}
              >
                Refresh
              </Button>
            </HStack>
            {rows.length === 0 ? (
              <Text color="fg.muted" fontSize="sm">
                No reactions defined yet.
              </Text>
            ) : (
              <Box overflowX="auto">
                <Table.Root
                  size="sm"
                  variant="line"
                  css={{
                    '& thead tr, & tbody tr': {
                      backgroundColor: 'var(--input-base-background-color)',
                    },
                  }}
                >
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader>ID</Table.ColumnHeader>
                      <Table.ColumnHeader>Emoji</Table.ColumnHeader>
                      <Table.ColumnHeader textAlign="end"> </Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {rows.map((row) => (
                      <Table.Row key={row.id}>
                        <Table.Cell fontWeight="medium">{row.id}</Table.Cell>
                        <Table.Cell fontSize="lg">{row.value}</Table.Cell>
                        <Table.Cell textAlign="end">
                          <Button
                            size="xs"
                            variant="outline"
                            colorPalette="red"
                            loading={deletingId === row.id}
                            onClick={() => {
                              void handleDelete(row.id);
                            }}
                          >
                            Delete
                          </Button>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              </Box>
            )}
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
}
